// Referrals sent — who this user invited, and what came of it.
//
// Was a collapsed accordion whose header carried the counts as prose and whose body
// repeated them as four stacked pairs. Now a `SectionCard`: the four numbers as
// `Figure`s across the top, one hairline row per send underneath. The rules for what
// counts as sent / registered / redeemed, and for collapsing anonymous link shares
// into one summary row, are untouched.
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IdentityCell, Skeleton, Stack, TintedStatusPill, Typography } from '../../../ui/core';
import { scrollY } from '../../../ui/styles';
import { CircleIcon, DollarBadgeIcon } from '../../../ui/icons';
import { useFormatters } from '../../../hooks';
import { getReferrals } from '../../../redux/Users/actions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import { Row, Figure, Figures } from '../../../pages/UserProfile/parts';

// 'Invited' hasn't happened yet, so it gets a quiet neutral tint (palette:
// null) instead of borrowing one of the semantic (success/accent) hues.
// Rendering is delegated to the shared TintedStatusPill (also used by the
// Fleet status chip) so this recipe isn't re-derived per caller.
const STATUS_CONFIG = {
  Invited: { palette: null, label: 'Invited' },
  Registered: { palette: 'accent', label: 'Registered' },
  Redeemed: { palette: 'success', label: 'Redeemed' },
};

const ReferralStatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.Invited;
  return <TintedStatusPill palette={cfg.palette} label={cfg.label} />;
};

// We only reliably learn who a referral went to once they register — a sent
// invite is just a share link / code with no recipient identity attached. So
// only surface a name when one is actually known; otherwise say so plainly
// rather than inventing a placeholder person.
const recipientName = (referral) => {
  const name = [referral.recipientFirstName, referral.recipientLastName].filter(Boolean).join(' ').trim();
  return name || null;
};

// Turn the raw share channel (sms, whatsapp, copy_link, …) into a readable label.
const MEDIUM_LABELS = {
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  email: 'Email',
  copy: 'Copy link',
  copy_link: 'Copy link',
  more: 'Share sheet',
};
const humanizeMedium = (medium) => {
  if (!medium) {
    return null;
  }
  return MEDIUM_LABELS[medium.toLowerCase()] || medium.replace(/_/g, ' ');
};

// A link/code invite that went out but hasn't converted: no recipient identity,
// just a share channel. These are collapsed into one summary line so a heavy
// referrer's list isn't buried under anonymous "Link shared" rows.
const isPendingLinkShare = (referral) =>
  !recipientName(referral) &&
  !referral.recipientPhoneNumber &&
  !referral.registered &&
  !!referral.medium;

// Identity avatar size, kept in one place since the meta/incentive lines
// below indent to align under the name (avatar width + IdentityCell's gap).
const IDENTITY_SIZE = 28;
const IDENTITY_INDENT = `${IDENTITY_SIZE + 12}px`;

const ReferralItem = ({ referral, first }) => {
  const f = useFormatters();
  const name = recipientName(referral);
  const channel = humanizeMedium(referral.medium);
  // A row with no recipient identity but a share channel is a link/code invite
  // that went out and hasn't converted yet.
  const isLinkShare = !name && !referral.recipientPhoneNumber && !!referral.medium;
  const sentOn = referral.createdAt
    ? f.date.toLocaleString(referral.createdAt, f.date.DateFormats.DATE_MED)
    : null;
  const incentives = referral.incentives || [];

  const identity = name ? (
    <IdentityCell
      name={name}
      secondary={referral.recipientPhoneNumber || undefined}
      colorKey={referral.recipientUserId || referral.recipientPhoneNumber || name}
      size={IDENTITY_SIZE}
    />
  ) : null;

  return (
    <Row
      first={first}
      label={
        identity ? (
          // Name is known: once they've registered, link to their profile.
          referral.recipientUserId ? (
            <Link to={`/users/${referral.recipientUserId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              {identity}
            </Link>
          ) : (
            identity
          )
        ) : (
          // No name yet — don't imply we know who this is.
          <Typography fontStyle="italic" color="text.secondary" sx={{ fontSize: '13px' }}>
            {isLinkShare ? 'Link shared' : referral.registered ? 'Registered rider' : 'Not registered yet'}
          </Typography>
        )
      }
      meta={
        (channel || sentOn || incentives.length > 0) && (
          // Indented under the name when there is an avatar above it, so the meta
          // lines start where the name does rather than where the avatar does.
          <Box sx={{ pl: identity ? IDENTITY_INDENT : 0 }}>
            {[channel && `via ${channel}`, sentOn && `Sent ${sentOn}`].filter(Boolean).join(' · ')}
            {/* One inline run rather than a flex row of three boxes. As flex items the
                dot, the title and the verdict each wrapped as a unit, so on a phone
                "Referral credit · Earned" broke as "Referral ·" / "credit  Earned" — the
                separator stranded on the first line and the verdict beside the wrong
                word. Inline, it wraps as the sentence it reads as. */}
            {incentives.map((incentive, i) => (
              <Box key={incentive.title ?? i}>
                <CircleIcon
                  sx={{
                    fontSize: 9,
                    mr: '6px',
                    verticalAlign: 'baseline',
                    color: incentive.redeemed ? 'success.main' : 'text.disabled',
                  }}
                />
                <span>{incentive.title || 'Reward'}</span>
                <Typography
                  component="span"
                  sx={{ fontSize: '11.5px' }}
                  color={incentive.redeemed ? 'success.main' : 'text.secondary'}
                >
                  {' · '}
                  {incentive.redeemed ? 'Earned' : 'Pending'}
                </Typography>
              </Box>
            ))}
          </Box>
        )
      }
      right={<ReferralStatusPill status={referral.status} />}
    />
  );
};

// One collapsed row standing in for all outstanding link/code shares, with a
// per-channel tally so it stays informative.
const LinkSharesSummary = ({ shares }) => {
  const byChannel = {};
  shares.forEach((share) => {
    const label = humanizeMedium(share.medium) || 'Other';
    byChannel[label] = (byChannel[label] || 0) + 1;
  });
  const breakdown = Object.entries(byChannel)
    .map(([label, count]) => `${label} ${count}`)
    .join(' · ');

  return (
    <Row
      label={`${shares.length} link ${shares.length === 1 ? 'share' : 'shares'} awaiting sign-up`}
      meta={breakdown}
      right={<ReferralStatusPill status="Invited" />}
    />
  );
};

const ReferralsCard = ({ user }) => {
  const dispatch = useDispatch();
  const referrals = useSelector((state) => state.users.selection.referrals);
  const referralsError = useSelector((state) => state.users.selection.referralsError);

  useEffect(() => {
    getReferrals(user.id)(dispatch);
  }, [user.id, dispatch]);

  // Every row is one send: contact-list invites, link invites that converted,
  // and link invites still awaiting sign-up. So the list length is true
  // outreach. A recipient counts as "registered" once they've created an
  // account, and "redeemed" once the sender has earned a reward.
  const sentCount = referrals ? referrals.length : 0;
  const registeredCount = referrals ? referrals.filter((r) => r.registered).length : 0;
  const redeemedCount = referrals ? referrals.filter((r) => r.redeemed).length : 0;
  const conversionRate = sentCount > 0 ? Math.round((registeredCount / sentCount) * 100) : 0;

  // Referrals with a real recipient get their own row; outstanding link shares
  // collapse into a single summary line at the end.
  const linkShares = referrals ? referrals.filter(isPendingLinkShare) : [];
  const detailRows = referrals ? referrals.filter((r) => !isPendingLinkShare(r)) : [];

  return (
    <SectionCard
      title="Referrals sent"
      icon={<DollarBadgeIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
    >
      {!referrals && !referralsError && (
        <Stack spacing={0.5} sx={{ px: 1.75, py: 1.5 }}>
          <Skeleton variant="rectangular" height={22} />
          <Skeleton variant="rectangular" height={22} />
        </Stack>
      )}
      {!referrals && referralsError && (
        // Distinct from the empty state: we couldn't load the data, so don't
        // imply the user has zero referrals.
        <EmptyRows>
          <Typography component="span" color="error.main" sx={{ fontSize: '12px' }}>
            Couldn&apos;t load referrals for this user. Refresh to try again.
          </Typography>
        </EmptyRows>
      )}
      {referrals && (
        // `Figures`, not a wrapping `Stack` — the same grid the other three figure cards
        // use. A `Stack`'s `spacing` is a left margin on every child after the first, and
        // a wrapped line's leading item is still one of those, so the second line was
        // indented 32px past the first and CONVERSION sat under REGISTERED rather than
        // under SENT. The grid keeps the columns.
        <Figures>
          <Figure label="Sent" value={sentCount} />
          <Figure label="Registered" value={registeredCount} />
          <Figure label="Redeemed" value={redeemedCount} />
          <Figure label="Conversion" value={`${conversionRate}%`} />
        </Figures>
      )}
      {referrals && referrals.length === 0 && <EmptyRows>This user hasn&apos;t sent any referrals.</EmptyRows>}
      {referrals && referrals.length > 0 && (
        <Box sx={{ maxHeight: 300, ...scrollY }}>
          {detailRows.map((referral) => (
            <ReferralItem key={referral.id} referral={referral} />
          ))}
          {linkShares.length > 0 && <LinkSharesSummary shares={linkShares} />}
        </Box>
      )}
    </SectionCard>
  );
};

export default ReferralsCard;
